import {
  ActionMenuItem,
  ControlledNumberField,
  ControlledSelect,
  ControlledStringDateOnlyPicker,
  FieldGrid,
} from '@if/mui'
import { parseISO } from '@if/core/date'
import { EPaymentPlan, PlanPromoRule } from '@if/core/types'
import { formatDate } from '@if/core/utils'
import { programConfigByFinancingProgramId } from '@src/data/creditapp-selectors'
import { isVariableInterest } from '@src/data/worksheet-selectors'
import { useDisabledPromoCodesList } from '@src/hooks/promo-code-hooks'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { SelectValueListItem } from '@src/types/SelectValueListItem'
import { EditPersonalLoanWorksheetDto } from '@src/types/WorksheetSchema'
import { useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface Props {
  creditApplication: CreditApplication
  merchantPayments: { label: string; value: string; id: string }[]
  disabledAmountInput?: boolean
  maxTermDuration?: number
  loanTermChoicesList: SelectValueListItem[]
  isLoadingTerm: boolean
  planPromoRules?: PlanPromoRule[]
  firstPaymentDateOptions: Date[]
  onPaymentPlanChange?: (paymentPlanId: EPaymentPlan | null) => void
  isBuydownEnabled: boolean
}

const WorksheetDetails = ({
  creditApplication,
  merchantPayments,
  disabledAmountInput,
  loanTermChoicesList,
  isLoadingTerm,
  planPromoRules,
  firstPaymentDateOptions,
  onPaymentPlanChange,
  isBuydownEnabled,
}: Props) => {
  const { t } = useTranslation()
  const { control } = useFormContext<EditPersonalLoanWorksheetDto>()
  const [paymentPlanId] = useWatch({ control, name: ['paymentPlanId'] })
  const firstPaymentDateOptionItems = useMemo(
    () =>
      firstPaymentDateOptions?.map((item) => {
        const formattedDate = formatDate(item)
        return (
          <ActionMenuItem key={formattedDate} value={formattedDate}>
            {formattedDate}
          </ActionMenuItem>
        )
      }),
    [firstPaymentDateOptions],
  )

  const disabledPromoList = useDisabledPromoCodesList(
    planPromoRules,
    creditApplication.normsCreditDecision,
    creditApplication.financingProgramId,
  )

  const merchantPaymentOptions = useMemo(
    () =>
      merchantPayments?.map((item) => (
        <ActionMenuItem
          key={item.value}
          value={item.value}
          disabled={(disabledPromoList ?? new Set<string>()).has(item.label.split(' ')[0])}
        >
          {item.label.includes(EPaymentPlan.RegularDailyInterests)
            ? t(`EPaymentPlan.${item.label as EPaymentPlan.RegularDailyInterests}`, { ns: 'enum' })
            : item.label}
        </ActionMenuItem>
      )),
    [disabledPromoList, merchantPayments, t],
  )

  const hasVariableInterest = isVariableInterest(paymentPlanId)
  const programConfig = programConfigByFinancingProgramId(creditApplication.financingProgramId)
  const availableFrequencyPayments = programConfig.frequencyPayments

  const termOptions = useMemo(
    () =>
      loanTermChoicesList.map((loanTerm) => (
        <ActionMenuItem key={loanTerm.value} value={loanTerm.value}>
          {loanTerm.label}
        </ActionMenuItem>
      )),
    [loanTermChoicesList],
  )

  const paymentFrequencyOptions = useMemo(
    () =>
      availableFrequencyPayments.map((frequency) => (
        <ActionMenuItem key={frequency} value={frequency}>
          {t(`EPaymentFrequency.${frequency}`, { ns: 'enum' })}
        </ActionMenuItem>
      )),
    [availableFrequencyPayments, t],
  )
  return (
    <FieldGrid columns={isBuydownEnabled ? { xs: 2, md: 4 } : { xs: 2, md: 3 }}>
      <ControlledNumberField
        label={t('worksheet.requestedAmount')}
        control={control}
        name="amountRequested"
        isMoney
        disabled={disabledAmountInput}
      />
      <ControlledStringDateOnlyPicker
        name="deliveryOn"
        control={control}
        label={t('worksheet.activationDate')}
        disablePast
        minDate={new Date()}
        maxDate={parseISO(formatDate(creditApplication.expiresOn))}
        fullWidth
      />
      {!isBuydownEnabled && (
        <ControlledSelect
          control={control}
          name="paymentPlanId"
          options={merchantPaymentOptions}
          label={t('worksheet.paymentPlan')}
          onChange={onPaymentPlanChange}
          disableEmptyOption
          fullWidth
        />
      )}
      <ControlledSelect
        control={control}
        name="paymentFrequency"
        label={t('worksheet.paymentFrequency')}
        options={paymentFrequencyOptions}
        disableEmptyOption
        fullWidth
      />
      <ControlledSelect
        control={control}
        name="firstPaymentOn"
        options={firstPaymentDateOptionItems}
        label={t('worksheet.firstPaymentOn')}
        disableEmptyOption
        fullWidth
      />
      {!isBuydownEnabled && (
        <ControlledSelect
          control={control}
          name="term"
          label={t('worksheet.loanTerm')}
          options={termOptions}
          disabled={isLoadingTerm || hasVariableInterest}
          disableEmptyOption
          fullWidth
        />
      )}
    </FieldGrid>
  )
}

export default WorksheetDetails
